import { Dispatch, RefObject, useEffect } from 'react';
import { SegmentedControl, useSignal } from '@asset-metrix/portal-ui';
import { SegmentedControlItem } from '@mantine/core';
import { View } from 'vega';

import { ExtractedBinding } from './inputBinding/extraction';
import { VegaControlAction, VegaState } from './inputBinding/vegaControlState';

import styles from './vegaChartSignalControls.module.scss';

type VegaChartSignalControlsProps = {
  bindings: ExtractedBinding[];
  controlDispatch: Dispatch<VegaControlAction>;
  controlState: VegaState;
  labelData: { key: string; label: string }[];
  vegaViewRef: RefObject<View | null>;
};

export const VegaChartSignalControls = ({
  bindings,
  controlDispatch,
  controlState,
  labelData,
  vegaViewRef,
}: VegaChartSignalControlsProps) => {
  return (
    <div className={styles['row']}>
      {bindings.map((binding) => (
        <RadioControlGroup
          key={binding.signalName}
          binding={binding}
          controlDispatch={controlDispatch}
          value={controlState[binding.signalName]?.value}
          labelData={labelData}
          vegaViewRef={vegaViewRef}
        />
      ))}
    </div>
  );
};

type RadioControlGroupProps = {
  binding: ExtractedBinding;
  controlDispatch: Dispatch<VegaControlAction>;
  labelData: { key: string; label: string }[];
  value?: string;
  vegaViewRef: RefObject<View | null>;
};

const RadioControlGroup = ({
  binding,
  controlDispatch,
  labelData,
  value,
  vegaViewRef,
}: RadioControlGroupProps) => {
  const signal = useSignal(vegaViewRef);

  const options: SegmentedControlItem[] = binding.options.map(
    (option, idx) => ({
      label:
        labelData.find((ld) => ld.key === option)?.label ??
        binding.fallbackLabels[idx],
      value: option,
    })
  );

  useEffect(() => {
    signal(binding.signalName, value);
  }, [binding.signalName, signal, value]);

  return (
    <SegmentedControl
      value={value}
      onChange={(newValue) => {
        controlDispatch({
          type: 'onChangeRadio',
          payload: { name: binding.signalName, value: newValue },
        });
      }}
      options={options}
    />
  );
};
